import apiEndPoints from '../../apiconstants';
import { api, handleErrorResponse } from '../api';
import type {
  CreateNotificationConfigPayload,
  CreateNotificationConfigResponse,
  DeleteNotificationConfigPayload,
  DeleteNotificationConfigResponse,
  GetNotificationCatalogParams,
  GetNotificationConfigsParams,
  GetNotificationConfigsResponse,
  GetWatchlistResponse,
  NotificationCatalogByTypeResponse,
  NotificationCatalogEntry,
  NotificationCatalogListResponse,
  NotificationConfigRecord,
  NotificationGrammar,
  UpdateNotificationConfigPayload,
  UpdateNotificationConfigResponse,
  UpdateWatchlistSubscriptionPayload,
  UpdateWatchlistSubscriptionResponse,
  WatchlistSubscriptionRecord,
} from './notificationTypes';

function unwrapCatalogPayload(response: any): {
  data: NotificationCatalogEntry | NotificationCatalogEntry[];
  grammar: NotificationGrammar;
} {
  const payload = response?.data ?? response;
  return {
    data: payload?.data,
    grammar: payload?.grammar,
  };
}

function unwrapDataArray<T>(response: any): T[] {
  const payload = response?.data ?? response;
  const items = payload?.data ?? payload;
  return Array.isArray(items) ? items : [];
}

export const notificationApiSlice = api.injectEndpoints({
  endpoints: (builder) => ({
    getNotificationCatalog: builder.query<
      NotificationCatalogByTypeResponse | NotificationCatalogListResponse,
      GetNotificationCatalogParams | void
    >({
      query: (params) => {
        const notificationType = params?.notification_type;
        const search =
          notificationType != null && String(notificationType).length > 0
            ? `?notification_type=${encodeURIComponent(String(notificationType))}`
            : '';
        return {
          url: `${apiEndPoints?.getNotificationCatalog}${search}`,
          method: 'GET',
        };
      },
      transformResponse: (response: any, _meta, arg) => {
        const { data, grammar } = unwrapCatalogPayload(response);
        const notificationType = arg?.notification_type;
        if (notificationType) {
          const catalog = Array.isArray(data) ? data[0] : data;
          return {
            catalog: catalog as NotificationCatalogEntry,
            grammar,
          } satisfies NotificationCatalogByTypeResponse;
        }
        let catalogList: NotificationCatalogEntry[] = [];
        if (Array.isArray(data)) {
          catalogList = data;
        } else if (data) {
          catalogList = [data];
        }
        return {
          catalog: catalogList,
          grammar,
        } satisfies NotificationCatalogListResponse;
      },
      transformErrorResponse: handleErrorResponse,
    }),
    getNotificationConfigs: builder.query<GetNotificationConfigsResponse, GetNotificationConfigsParams | void>({
      query: (params) => {
        const notificationType = params?.notification_type;
        const search =
          notificationType != null && String(notificationType).length > 0
            ? `?notification_type=${encodeURIComponent(String(notificationType))}`
            : '';
        return {
          url: `${apiEndPoints?.getNotificationConfig}${search}`,
          method: 'GET',
        };
      },
      transformResponse: (response: any) => {
        const payload = response?.data ?? response;
        const data = unwrapDataArray<NotificationConfigRecord>(response);
        return {
          data,
          total: typeof payload?.total === 'number' ? payload.total : data.length,
        } satisfies GetNotificationConfigsResponse;
      },
      transformErrorResponse: handleErrorResponse,
      providesTags: ['NotificationConfigs'],
    }),
    createNotificationConfig: builder.mutation<
      CreateNotificationConfigResponse,
      CreateNotificationConfigPayload
    >({
      query: (body) => ({
        url: apiEndPoints?.getNotificationConfig,
        method: 'POST',
        body,
      }),
      transformResponse: (response: any) => {
        const payload = response?.data ?? response;
        return {
          data: payload?.data ?? payload,
          message: payload?.message ?? response?.message,
        } satisfies CreateNotificationConfigResponse;
      },
      transformErrorResponse: handleErrorResponse,
      invalidatesTags: ['NotificationConfigs'],
    }),
    updateNotificationConfig: builder.mutation<
      UpdateNotificationConfigResponse,
      UpdateNotificationConfigPayload
    >({
      query: ({ config_id, ...body }) => ({
        url: `${apiEndPoints?.getNotificationConfig}/${encodeURIComponent(config_id)}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: any) => {
        const payload = response?.data ?? response;
        return {
          data: payload?.data ?? payload,
          message: payload?.message ?? response?.message,
        } satisfies UpdateNotificationConfigResponse;
      },
      transformErrorResponse: handleErrorResponse,
      invalidatesTags: ['NotificationConfigs'],
    }),
    deleteNotificationConfig: builder.mutation<
      DeleteNotificationConfigResponse,
      DeleteNotificationConfigPayload
    >({
      query: ({ config_id }) => ({
        url: `${apiEndPoints?.getNotificationConfig}/${encodeURIComponent(config_id)}`,
        method: 'DELETE',
      }),
      transformResponse: (response: any) => {
        const payload = response?.data ?? response;
        return {
          data: payload?.data ?? payload,
          message: payload?.message ?? response?.message,
        } satisfies DeleteNotificationConfigResponse;
      },
      transformErrorResponse: handleErrorResponse,
      invalidatesTags: ['NotificationConfigs'],
    }),
    getWatchlist: builder.query<GetWatchlistResponse, void>({
      query: () => ({
        url: apiEndPoints?.getNotificationWatchlist,
        method: 'GET',
      }),
      transformResponse: (response: any) => ({
        data: unwrapDataArray<WatchlistSubscriptionRecord>(response),
      }),
      providesTags: ['Watchlist'],
      transformErrorResponse: handleErrorResponse,
    }),
    updateWatchlistSubscription: builder.mutation<
      UpdateWatchlistSubscriptionResponse,
      UpdateWatchlistSubscriptionPayload
    >({
      query: ({ config_id, state, user_id, email }) => ({
        url: `${apiEndPoints?.getNotificationWatchlist}/${encodeURIComponent(config_id)}`,
        method: 'PATCH',
        body: {
          state,
          ...(user_id ? { user_id } : {}),
          ...(email ? { email } : {}),
        },
      }),
      transformResponse: (response: any) => {
        const payload = response?.data ?? response;
        return {
          data: payload?.data ?? payload,
          message: payload?.message ?? response?.message,
        } satisfies UpdateWatchlistSubscriptionResponse;
      },
      invalidatesTags: ['Watchlist'],
      transformErrorResponse: handleErrorResponse,
    }),
  }),
});

export const {
  useGetNotificationCatalogQuery,
  useLazyGetNotificationCatalogQuery,
  useGetNotificationConfigsQuery,
  useLazyGetNotificationConfigsQuery,
  useCreateNotificationConfigMutation,
  useUpdateNotificationConfigMutation,
  useDeleteNotificationConfigMutation,
  useGetWatchlistQuery,
  useUpdateWatchlistSubscriptionMutation,
} = notificationApiSlice;
